import React, { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { isAxiosError } from "axios";
import { getSharedSourceOperators } from "../../../services/grantsApi";
import OperatorProjectTree from "./OperatorProjectTree";

/** Stale time for shared source operators query (1 minute) */
const SHARED_OPERATORS_STALE_TIME_MS = 60_000;

/**
 * Props for SharedSourceOperatorsSection component
 * @see docs/specifications/2026-03-17-shared-source-operators-section.md
 * @see docs/specifications/2026-06-05-shared-source-operators-expand-projects/spec.md
 */
interface SharedSourceOperatorsSectionProps {
  operatorId: string;
}

/**
 * Chevron icon for expandable section
 */
const ChevronIcon: React.FC<{ expanded: boolean }> = ({ expanded }) => (
  <svg
    className={`h-5 w-5 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    aria-hidden="true"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

/**
 * Skeleton loader for operators list (2 rows, multi-level structure)
 */
const OperatorsSkeleton: React.FC = () => (
  <div className="space-y-4" role="status" aria-label="Ładowanie operatorów">
    {[1, 2].map((i) => (
      <div key={i} className="animate-pulse space-y-2">
        <div className="h-4 bg-neutral-200 rounded w-2/3"></div>
        <div className="pl-4 space-y-1">
          <div className="h-3 bg-neutral-200 rounded w-1/2"></div>
          <div className="h-3 bg-neutral-200 rounded w-3/4"></div>
        </div>
      </div>
    ))}
  </div>
);

/**
 * Error alert with retry button (compact version)
 */
const ErrorAlert: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm">
    <div className="flex items-center justify-between">
      <span className="text-red-700">{message}</span>
      <button
        onClick={onRetry}
        aria-label="Odśwież listę operatorów współdzielących źródła"
        className="px-2 py-1 text-xs font-medium text-red-700 bg-white border border-red-300 rounded hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500"
      >
        Odśwież
      </button>
    </div>
  </div>
);

/**
 * Collapsible section displaying operators that share information sources
 * with the current operator. Eagerly loads data on mount.
 * Each operator is rendered with its nested project tree (projects → calls → articles).
 *
 * @see docs/specifications/2026-03-17-shared-source-operators-section.md
 * @see docs/specifications/2026-06-05-shared-source-operators-expand-projects/spec.md
 */
const SharedSourceOperatorsSection: React.FC<SharedSourceOperatorsSectionProps> = ({
  operatorId,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  // Eager loading - fetch immediately on mount (not lazy)
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["shared-source-operators", operatorId],
    queryFn: () => getSharedSourceOperators(operatorId),
    enabled: !!operatorId,
    staleTime: SHARED_OPERATORS_STALE_TIME_MS,
  });

  const operators = data?.operators || [];
  const totalCount = data?.totalCount ?? 0;

  // Determine if data is truncated (server limits to 20)
  const hasMoreOperators = totalCount > operators.length;

  // Focus management after toggle
  const pendingFocusRef = useRef<"content" | "button" | null>(null);

  const handleToggle = () => {
    setIsExpanded((prev) => {
      pendingFocusRef.current = !prev ? "content" : "button";
      return !prev;
    });
  };

  useEffect(() => {
    if (pendingFocusRef.current === "content" && contentRef.current) {
      contentRef.current.focus();
      pendingFocusRef.current = null;
    } else if (pendingFocusRef.current === "button" && toggleRef.current) {
      toggleRef.current.focus();
      pendingFocusRef.current = null;
    }
  }, [isExpanded]);

  // Error message mapping
  const getErrorMessage = (err: unknown): string | null => {
    // 403 - hide section
    if (isAxiosError(err) && err.response?.status === 403) {
      return null;
    }

    if (err instanceof Error) {
      if (err.name === "TimeoutError" || err.message.includes("timeout")) {
        return "Przekroczono czas oczekiwania";
      }
      if (err.message.includes("network") || err.message.includes("Network")) {
        return "Błąd połączenia";
      }
    }
    return "Nie udało się pobrać operatorów";
  };

  // Hide section if 403 error
  const errorMessage = error ? getErrorMessage(error) : null;
  if (error && errorMessage === null) {
    return null;
  }

  return (
    <section
      aria-labelledby="shared-source-operators-heading"
      className="bg-white rounded-lg shadow-sm border border-neutral-200"
    >
      <div className="px-6 py-4">
        <h2 id="shared-source-operators-heading" className="contents">
          <button
            ref={toggleRef}
            onClick={handleToggle}
            className="flex items-center justify-between w-full text-left"
            aria-expanded={isExpanded}
            aria-controls="shared-source-operators-content"
            id="shared-source-operators-toggle"
          >
            <span className="text-lg font-medium text-neutral-900">
              Operatorzy współdzielący źródła
            </span>
            <ChevronIcon expanded={isExpanded} />
          </button>
        </h2>

        <div
          ref={contentRef}
          id="shared-source-operators-content"
          aria-labelledby="shared-source-operators-toggle"
          aria-live="polite"
          tabIndex={-1}
          className={`mt-4 pt-4 border-t border-neutral-200 ${isExpanded ? "" : "hidden"}`}
        >
          {isLoading && <OperatorsSkeleton />}

          {error && !isLoading && errorMessage !== null && (
            <ErrorAlert message={errorMessage} onRetry={() => refetch()} />
          )}

          {!isLoading && !error && operators.length === 0 && (
            <p className="text-center text-sm text-neutral-500 py-2">
              Brak operatorów współdzielących źródła
            </p>
          )}

          {!isLoading && !error && operators.length > 0 && (
            <>
              <div className="divide-y divide-neutral-200">
                {operators.map((operator) => (
                  <div key={operator.id} className="py-3 first:pt-0 last:pb-0">
                    <Link
                      to={`/grants/operators/${operator.id}`}
                      className="text-sm text-primary-600 hover:text-primary-800 hover:underline font-medium focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
                    >
                      {operator.name}
                    </Link>
                    <div className="mt-2 pl-3 border-l border-neutral-200">
                      {operator.projects.length === 0 ? (
                        <p className="text-xs text-neutral-400 italic">Brak projektów</p>
                      ) : (
                        <OperatorProjectTree projects={operator.projects} />
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {hasMoreOperators && (
                <p className="mt-2 text-xs text-neutral-500 text-center">
                  Pokazano {operators.length} z {totalCount} operatorów (limit: 20)
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default SharedSourceOperatorsSection;
